/**
 * ET - Cart gift
 * ---------------------------------------------------------------------------
 * Campaign free gift with purchase (Theme settings > ET - Campaign). Loaded by
 * layout/theme.liquid only while settings.et_gift_enabled is on, together with
 * the #et-cart-gift-config JSON rendered by snippets/et-cart-gift.liquid.
 *
 * Client rules:
 *   - qualifying total = cart total after discounts, minus the gift line(s)
 *   - qualifying >= threshold: the gift is added automatically
 *   - the customer removes the gift: it is never re-added on its own, the block
 *     offers an add button instead
 *   - qualifying < threshold: the gift is removed
 *   - gift sold out: "unavailable" state, the gift is never added
 *
 * How it stays correct on a busy store:
 *   - ONE planner decides from a fresh /cart.js read, and every cart write runs
 *     through ONE queue: one write per step, then re-planned on the cart that the
 *     write returned. Line keys are never kept across awaits, because
 *     shipping-property-sync.js re-keys lines after every cart change.
 *   - The cart attribute _et_gift records what the campaign did:
 *       "added"    the gift is supposed to be in the cart
 *       "declined" the customer removed it, it is only added on request
 *     A gift that is missing while the mark says "added" and the cart is still
 *     above the threshold was removed by the customer. That also covers the
 *     cart page, which reloads instead of firing events. The campaign clears
 *     "added" BEFORE it removes the gift itself, so its own removals are never
 *     mistaken for the customer's.
 *   - Writes are capped per minute and cart page reloads are capped, so a third
 *     party fighting over the gift line can never cause a loop.
 */
(function () {
  'use strict';

  if (window.etCartGift) return;

  const configElement = document.getElementById('et-cart-gift-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const MARK_ATTRIBUTE = '_et_gift';
  const MARK_ADDED = 'added';
  const MARK_DECLINED = 'declined';
  const GIFT_PROPERTY = '_is_free_gift';
  const BUNDLE_GIFT_PROPERTY = '_kop_bundle_gift';

  const RECONCILE_DELAY = 150;
  const MAX_STEPS = 6;
  const WRITE_LIMIT = 12;
  const WRITE_WINDOW = 60000;
  const REQUEST_TIMEOUT = 10000;
  const RELOAD_STORAGE_KEY = 'et-cart-gift:reloads';
  const RELOAD_LIMIT = 3;
  const RELOAD_WINDOW = 30000;

  /* A cart line seen only through the Liquid snapshot: it exists, nothing more is known. */
  const SNAPSHOT_LINE = Object.freeze({ key: null, quantity: 1, product_id: null, variant_id: null, properties: {} });

  /* Shopify money format placeholders: [decimals, thousands separator, decimal separator]. */
  const MONEY_STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ''],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };

  const shopify = window.Shopify || {};
  const routesRoot = (shopify.routes && shopify.routes.root) || '/';
  const activeCurrency = (shopify.currency && shopify.currency.active) || config.currency || '';
  const isCartPage = Boolean(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');

  const productId = toId(config.productId);
  const giftVariantId = toId(config.variantId);
  const campaignVariantIds = new Set((config.variantIds || []).map(toId));
  const soldOutVariantIds = new Set((config.soldOutVariantIds || []).map(toId));
  const thresholdCents = resolveThreshold();
  const shippingCents = convertFromShopCurrency(config.shippingEurCents);
  const formatMoney = createMoneyFormatter();

  const elements = new Set();
  const writeLog = [];

  let currentView = null;
  let pendingDecline = false;
  let addBlocked = false;
  let queue = Promise.resolve();
  let reconcileTimer = 0;
  let reconcileQueued = false;
  let customerActionRunning = false;
  let lastLineRequest = null;

  /* ------------------------------------------------------------------ helpers */

  function toId(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  /**
   * Threshold in the visitor's currency, in cents. EUR is the campaign currency,
   * other markets get Shopify.currency.rate applied, rounded to whole units.
   * Null when it cannot be known, in which case the campaign does nothing.
   */
  function resolveThreshold() {
    const eurCents = Number(config.thresholdEurCents);
    if (!Number.isFinite(eurCents) || eurCents <= 0 || !activeCurrency) return null;
    if (activeCurrency === 'EUR') return Math.round(eurCents);

    const rate = parseFloat(shopify.currency && shopify.currency.rate);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return Math.round((eurCents / 100) * rate) * 100;
  }

  /**
   * An amount stored in the shop currency (the free shipping threshold), converted with the same rate as the theme's
   * own bar. Rounded up to whole units, so the marker reads like the gift threshold and never promises free shipping
   * before it is reached.
   */
  function convertFromShopCurrency(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount) || amount <= 0 || !activeCurrency) return null;
    if (activeCurrency === 'EUR') return Math.round(amount);

    const rate = parseFloat(shopify.currency && shopify.currency.rate);
    return Number.isFinite(rate) && rate > 0 ? Math.ceil((amount * rate) / 100) * 100 : null;
  }

  function stripTags(value) {
    return String(value || '').replace(/<[^>]*>/g, '');
  }

  function decodeEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function normaliseSpaces(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function applyMoneyFormat(cents, format, trimZeros) {
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const style = match && MONEY_STYLES[match[1]];
    if (!style) return null;

    const parts = (cents / 100).toFixed(trimZeros ? 0 : style[0]).split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, style[1]);
    return format.replace(match[0], parts[1] ? whole + style[2] + parts[1] : whole);
  }

  /**
   * Formats like Liquid's money_without_trailing_zeros, so the amount the JS
   * writes matches the server render. The shop's money format is only trusted
   * when it reproduces Liquid's own output for a sample amount; otherwise the
   * browser formats the active currency.
   */
  function createMoneyFormatter() {
    const format = decodeEntities(stripTags(config.moneyFormat));
    const sample = normaliseSpaces(decodeEntities(stripTags(config.moneySample)));
    const trusted = Boolean(format && sample) && normaliseSpaces(applyMoneyFormat(123456, format, false)) === sample;
    const locale = document.documentElement.lang || shopify.locale || undefined;

    return function (cents) {
      const value = Math.max(0, Math.round(Number(cents) || 0));
      const trimZeros = value % 100 === 0;

      if (trusted) {
        const formatted = applyMoneyFormat(value, format, trimZeros);
        if (formatted) return formatted;
      }

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: activeCurrency || 'EUR',
          minimumFractionDigits: trimZeros ? 0 : 2,
          maximumFractionDigits: 2
        }).format(value / 100);
      } catch (error) {
        return (value / 100).toFixed(trimZeros ? 0 : 2);
      }
    };
  }

  /* ------------------------------------------------------------------ cart analysis */

  function isCampaignLine(line) {
    return productId !== null && toId(line.product_id) === productId;
  }

  function isGiftLine(line) {
    const properties = (line && line.properties) || {};
    if (properties[BUNDLE_GIFT_PROPERTY]) return false;
    if (properties[GIFT_PROPERTY]) return true;
    return isCampaignLine(line);
  }

  /** A gift line of a sold out variant cannot go through checkout, so it is not kept. */
  function isKeepable(line) {
    return !(isCampaignLine(line) && soldOutVariantIds.has(toId(line.variant_id)));
  }

  function normaliseMark(value) {
    return value === MARK_ADDED || value === MARK_DECLINED ? value : '';
  }

  function analyse(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const giftLines = items.filter(isGiftLine);
    const giftCents = giftLines.reduce(function (sum, line) {
      return sum + (Number(line.final_line_price) || 0);
    }, 0);

    return {
      giftLines: giftLines,
      qualifying: Math.max(0, (Number(cart && cart.total_price) || 0) - giftCents),
      mark: normaliseMark(cart && cart.attributes && cart.attributes[MARK_ATTRIBUTE])
    };
  }

  function analyseSnapshot(snapshot, currency) {
    const qualifying = Number(snapshot.qualifying);
    if (!Number.isFinite(qualifying)) return null;
    if (currency && activeCurrency && currency !== activeCurrency) return null;

    return {
      giftLines: snapshot.present === true || snapshot.present === 'true' ? [SNAPSHOT_LINE] : [],
      qualifying: qualifying,
      mark: normaliseMark(snapshot.mark)
    };
  }

  /* ------------------------------------------------------------------ planner */

  function makeView(state, analysis) {
    const qualifying = analysis ? analysis.qualifying : null;
    return {
      state: state,
      qualifyingCents: qualifying,
      thresholdCents: thresholdCents,
      remainingCents: thresholdCents !== null && qualifying !== null ? Math.max(0, thresholdCents - qualifying) : null
    };
  }

  function step(state, analysis, action) {
    return { view: makeView(state, analysis), action: action || null };
  }

  function setMark(value) {
    return { type: 'mark', value: value };
  }

  /* announce: also fetch the sections the theme bundles with its own line changes, so the write can be announced as a
     cart:change (see recoverRejectedDecrease). */
  function setQuantity(line, quantity, announce) {
    return { type: 'change', key: line.key, quantity: quantity, announce: Boolean(announce) };
  }

  /**
   * The single decision table. Pure apart from reading the module flags:
   * returns what the customer should see and, when the cart is not yet in that
   * state, the ONE next write that moves it there.
   */
  function plan(analysis) {
    if (thresholdCents === null || productId === null) return step('off', analysis);

    const giftLines = analysis.giftLines;
    const mark = analysis.mark;
    const declined = mark === MARK_DECLINED || pendingDecline;
    const canAdd = config.available === true && giftVariantId !== null && !addBlocked;

    /* Under the threshold: no gift. The "added" mark is cleared first, so the removal reads as ours.
       A sold out gift is announced as such rather than promised for a higher cart value. */
    if (analysis.qualifying < thresholdCents) {
      const belowState = canAdd ? 'remaining' : 'unavailable';
      const targetMark = declined ? MARK_DECLINED : '';
      if (mark !== targetMark) return step(belowState, analysis, setMark(targetMark));
      if (giftLines.length) return step(belowState, analysis, setQuantity(giftLines[0], 0));
      return step(belowState, analysis);
    }

    /* Above the threshold with a gift: keep exactly one, quantity one, and mark it as added. */
    const keepable = giftLines.filter(isKeepable);
    if (keepable.length) {
      const keep = keepable.find(isCampaignLine) || keepable[0];
      const extra = giftLines.find(function (line) { return line !== keep; });
      if (extra) return step('added', analysis, setQuantity(extra, 0));
      if (keep.key && Number(keep.quantity) !== 1) return step('added', analysis, setQuantity(keep, 1));
      if (mark !== MARK_ADDED) return step('added', analysis, setMark(MARK_ADDED));
      return step('added', analysis);
    }

    const absentState = declined ? (canAdd ? 'declined' : 'unavailable') : (canAdd ? 'adding' : 'unavailable');

    /* Only sold out gift lines left: the campaign takes them out itself, mark first. */
    if (giftLines.length) {
      if (mark === MARK_ADDED) return step(absentState, analysis, setMark(''));
      return step(absentState, analysis, setQuantity(giftLines[0], 0));
    }

    /* No gift although it was added (or declined): the customer removed it. Never re-add on our own. */
    if (declined || mark === MARK_ADDED) {
      const state = canAdd ? 'declined' : 'unavailable';
      if (mark !== MARK_DECLINED) return step(state, analysis, setMark(MARK_DECLINED));
      return step(state, analysis);
    }

    if (!canAdd) return step('unavailable', analysis);
    return step('adding', analysis, { type: 'add' });
  }

  /* ------------------------------------------------------------------ network */

  async function request(path, method, body) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT) : 0;

    try {
      const response = await fetch(routesRoot + path, {
        method: method,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: body
          ? { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller ? controller.signal : undefined
      });

      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }
      return { ok: response.ok, status: response.status, data: data };
    } catch (error) {
      return { ok: false, status: 0, data: null };
    } finally {
      clearTimeout(timer);
    }
  }

  async function readCart() {
    const response = await request('cart.js', 'GET');
    return response.ok && response.data && Array.isArray(response.data.items) ? response.data : null;
  }

  function isCart(data) {
    return Boolean(data && Array.isArray(data.items));
  }

  function takeWriteSlot() {
    const now = Date.now();
    while (writeLog.length && now - writeLog[0] > WRITE_WINDOW) writeLog.shift();
    if (writeLog.length >= WRITE_LIMIT) {
      console.warn('[ET cart gift] write limit reached, pausing cart writes for a minute');
      return false;
    }
    writeLog.push(now);
    return true;
  }

  /** The section ids the theme asks Shopify to render along with its own cart writes (the drawer). */
  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  async function perform(action) {
    if (action.type === 'mark') {
      const attributes = {};
      attributes[MARK_ATTRIBUTE] = action.value;
      const response = await request('cart/update.js', 'POST', { attributes: attributes });
      return response.ok && isCart(response.data) ? { cart: response.data } : { stop: true };
    }

    if (action.type === 'change') {
      const body = { id: action.key, quantity: action.quantity };
      if (action.announce) body.sections = bundledSections();
      const response = await request('cart/change.js', 'POST', body);
      if (response.ok && isCart(response.data)) return { cart: response.data, linesChanged: true };
      /* A 4xx is almost always a line that was re-keyed meanwhile: re-read and re-plan. */
      return response.status >= 400 && response.status < 500 ? { cart: null, failed: true } : { stop: true };
    }

    const properties = {};
    properties[GIFT_PROPERTY] = true;
    const response = await request('cart/add.js', 'POST', { id: giftVariantId, quantity: 1, properties: properties });

    /* Refused (sold out, 422) or failed: show "unavailable" and stop trying on this page. */
    if (!response.ok) {
      addBlocked = true;
      return { cart: null };
    }

    /* An add that does not stick (something removed it straight away) is never retried in a loop. */
    const cart = await readCart();
    if (cart && !analyse(cart).giftLines.length) addBlocked = true;
    return { cart: cart, linesChanged: true };
  }

  /* ------------------------------------------------------------------ reconcile */

  function enqueue(task) {
    const run = queue.then(task).catch(function (error) {
      console.warn('[ET cart gift]', error);
    });
    queue = run;
    return run;
  }

  /** Plans, writes and re-plans until the cart matches the decision table. */
  async function settle(cart) {
    let linesChanged = false;
    let failures = 0;

    for (let index = 0; index < MAX_STEPS && cart; index += 1) {
      const analysis = analyse(cart);
      if (analysis.mark === MARK_DECLINED) pendingDecline = false;

      const decision = plan(analysis);
      publish(decision.view);
      if (!decision.action || !takeWriteSlot()) break;

      const outcome = await perform(decision.action);
      if (outcome.linesChanged) linesChanged = true;
      if (outcome.stop) break;
      if (outcome.failed && ++failures >= 2) break;

      cart = outcome.cart || (await readCart());
    }

    if (linesChanged) refreshCartUi();
  }

  function requestReconcile(delay) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      if (reconcileQueued) return;
      reconcileQueued = true;
      enqueue(async function () {
        reconcileQueued = false;
        await settle(await readCart());
      });
    }, typeof delay === 'number' ? delay : RECONCILE_DELAY);
  }

  /** Runs one customer request at a time, with every button of the campaign showing it is busy meanwhile. */
  function runCustomerAction(task) {
    if (customerActionRunning) return;
    customerActionRunning = true;
    elements.forEach(function (element) { element.setBusy(true); });

    enqueue(task).then(function () {
      customerActionRunning = false;
      elements.forEach(function (element) { element.setBusy(false); });
    });
  }

  /** The customer asks for the gift again: their earlier decline no longer stands. */
  function optIn() {
    runCustomerAction(async function () {
      pendingDecline = false;
      let cart = await readCart();

      if (cart && analyse(cart).mark === MARK_DECLINED && takeWriteSlot()) {
        const outcome = await perform(setMark(''));
        cart = outcome.cart || (await readCart());
      }

      await settle(cart);
    });
  }

  /** The customer deselects the gift card: recorded as declined first, then every gift line is taken out. */
  function optOut() {
    runCustomerAction(async function () {
      pendingDecline = true;
      let cart = await readCart();
      let linesChanged = false;

      if (cart && analyse(cart).mark !== MARK_DECLINED && takeWriteSlot()) {
        const outcome = await perform(setMark(MARK_DECLINED));
        cart = outcome.cart || (await readCart());
      }

      for (let index = 0; index < MAX_STEPS && cart; index += 1) {
        const giftLines = analyse(cart).giftLines;
        if (!giftLines.length || !takeWriteSlot()) break;

        const outcome = await perform(setQuantity(giftLines[0], 0));
        if (outcome.linesChanged) linesChanged = true;
        if (outcome.stop) break;
        cart = outcome.cart || (await readCart());
      }

      await settle(cart);
      if (linesChanged) refreshCartUi();
    });
  }

  function reserveReload() {
    const now = Date.now();
    let log = [];

    try {
      log = JSON.parse(sessionStorage.getItem(RELOAD_STORAGE_KEY) || '[]');
    } catch (error) {
      log = [];
    }

    log = Array.isArray(log) ? log.filter(function (time) { return now - time < RELOAD_WINDOW; }) : [];
    if (log.length >= RELOAD_LIMIT) return false;
    log.push(now);

    try {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, JSON.stringify(log));
    } catch (error) {
      return false;
    }
    return true;
  }

  /** The cart page re-renders by reloading (like the theme does); everywhere else the drawer refreshes itself. */
  function refreshCartUi() {
    if (isCartPage) {
      if (reserveReload()) window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /* ------------------------------------------------------------------ view */

  function publish(view) {
    const changed = !currentView
      || currentView.state !== view.state
      || currentView.qualifyingCents !== view.qualifyingCents
      || currentView.thresholdCents !== view.thresholdCents;

    currentView = view;
    elements.forEach(function (element) { element.render(view); });

    if (changed) {
      document.dispatchEvent(new CustomEvent('et-cart-gift:state', { detail: Object.assign({}, view) }));
    }
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  /**
   * Two parts share this element: the progress box (data-part="progress", add button) and the gift card
   * (data-part="card", a toggle: selected while the gift is in the cart). data-state drives what CSS shows.
   */
  class EtCartGiftElement extends HTMLElement {
    constructor() {
      super();
      this._onAddClick = this._onAddClick.bind(this);
      this._onToggleClick = this._onToggleClick.bind(this);
    }

    connectedCallback() {
      this.amountNodes = this.querySelectorAll('[data-et-gift-amount]');
      this.thresholdNodes = this.querySelectorAll('[data-et-gift-threshold]');
      this.shippingStop = this.querySelector('[data-et-gift-shipping]');
      this.shippingLabel = this.querySelector('[data-et-gift-shipping-amount]');
      this.addButton = this.querySelector('[data-et-gift-add]');
      this.toggle = this.querySelector('[data-et-gift-toggle]');
      if (this.addButton) this.addButton.addEventListener('click', this._onAddClick);
      if (this.toggle) this.toggle.addEventListener('click', this._onToggleClick);

      elements.add(this);

      /* The server render is the freshest truth for this element; the JS only adds the exchange rate. */
      const analysis = analyseSnapshot(this.dataset, this.dataset.currency);
      this.render(analysis ? plan(analysis).view : currentView);
      this.setBusy(customerActionRunning);

      requestReconcile();
    }

    disconnectedCallback() {
      elements.delete(this);
      if (this.addButton) this.addButton.removeEventListener('click', this._onAddClick);
      if (this.toggle) this.toggle.removeEventListener('click', this._onToggleClick);
    }

    _onAddClick(event) {
      event.preventDefault();
      optIn();
    }

    _onToggleClick(event) {
      event.preventDefault();
      const state = this.dataset.state;
      if (state === 'added' || state === 'adding') optOut();
      else if (state === 'declined') optIn();
    }

    render(view) {
      if (!view) return;
      if (this.dataset.state !== view.state) this.dataset.state = view.state;

      const known = view.thresholdCents !== null && view.qualifyingCents !== null;
      if (view.remainingCents !== null) {
        this.amountNodes.forEach(function (node) { setText(node, formatMoney(view.remainingCents)); });
      }
      if (view.thresholdCents !== null) {
        this.thresholdNodes.forEach(function (node) { setText(node, formatMoney(view.thresholdCents)); });
      }

      if (this.dataset.part === 'progress') {
        let progress = view.state === 'pending' || view.state === 'off' ? 0 : 1;
        if (view.state === 'remaining' && known && view.thresholdCents > 0) {
          progress = Math.max(0, Math.min(1, view.qualifyingCents / view.thresholdCents));
        }
        this.style.setProperty('--et-gift-progress', progress.toFixed(4));

        if (this.shippingStop) {
          const visible = shippingCents !== null && known && shippingCents < view.thresholdCents;
          this.shippingStop.hidden = !visible;
          if (visible) {
            this.style.setProperty('--et-gift-ship', (shippingCents / view.thresholdCents).toFixed(4));
            this.shippingStop.classList.toggle('is-reached', view.qualifyingCents >= shippingCents);
            setText(this.shippingLabel, formatMoney(shippingCents));
          }
        }
      }

      if (this.toggle) {
        const selected = view.state === 'added' || view.state === 'adding';
        this.toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
        this.toggle.disabled = customerActionRunning || !(selected || view.state === 'declined');
      }
    }

    setBusy(busy) {
      if (this.addButton) {
        this.addButton.disabled = busy;
        this.addButton.classList.toggle('is-loading', busy);
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      if (this.toggle) {
        const state = this.dataset.state;
        this.toggle.disabled = busy || !(state === 'added' || state === 'adding' || state === 'declined');
        this.toggle.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    }
  }

  /* ------------------------------------------------------------------ events */

  function lineItemOf(event) {
    return event.target instanceof Element ? event.target.closest('line-item') : null;
  }

  /** The <line-item> carries line-key="<variant id>:<hash>"; the variant id survives re-keying. */
  function isCampaignLineElement(element) {
    const key = element && element.getAttribute('line-key');
    return Boolean(key) && campaignVariantIds.has(toId(String(key).split(':')[0]));
  }

  function isAboveThreshold() {
    return Boolean(currentView)
      && currentView.thresholdCents !== null
      && currentView.qualifyingCents !== null
      && currentView.qualifyingCents >= currentView.thresholdCents;
  }

  /* Fires before the drawer's own request. For the gift line it marks the decline up front, so a reconcile running
     meanwhile can never re-add the gift; for every line it remembers what the customer asked for. */
  function onLineWillChange(event) {
    const lineItem = lineItemOf(event);
    if (!lineItem) return;

    const target = Number(event.detail && event.detail.targetQuantity);
    lastLineRequest = { key: lineItem.getAttribute('line-key'), target: target, at: Date.now() };

    if (isCampaignLineElement(lineItem) && target === 0 && isAboveThreshold()) pendingDecline = true;
  }

  function onLineError(event) {
    const lineItem = lineItemOf(event);
    const request = lastLineRequest;
    lastLineRequest = null;

    if (isCampaignLineElement(lineItem)) {
      pendingDecline = false;
      return;
    }
    if (lineItem && request && request.key === lineItem.getAttribute('line-key') && Date.now() - request.at < 15000) {
      recoverRejectedDecrease(request);
    }
  }

  /**
   * A cart validation rule on the shop (the ONYX gift validator) refuses any change that leaves the free gift in a
   * cart below its threshold, so a customer who holds the gift cannot lower their cart. Only for a lower quantity
   * (never an increase, which can fail for stock reasons that must stay visible): take the gift out as the
   * campaign's own removal, apply the customer's change again and refresh the drawer.
   */
  function recoverRejectedDecrease(request) {
    enqueue(async function () {
      let cart = await readCart();
      const line = cart && cart.items.find(function (item) { return item.key === request.key; });
      if (!line || !(request.target < Number(line.quantity)) || !analyse(cart).giftLines.length) return;

      if (analyse(cart).mark === MARK_ADDED && takeWriteSlot()) {
        const outcome = await perform(setMark(''));
        if (outcome.stop) return;
        cart = outcome.cart || (await readCart());
      }

      for (let index = 0; index < MAX_STEPS && cart; index += 1) {
        const giftLines = analyse(cart).giftLines;
        if (!giftLines.length || !takeWriteSlot()) break;
        const outcome = await perform(setQuantity(giftLines[0], 0));
        if (outcome.stop) break;
        cart = outcome.cart || (await readCart());
      }

      const current = cart && cart.items.find(function (item) { return item.key === request.key; });
      const applied = current && takeWriteSlot() ? await perform(setQuantity(current, request.target, true)) : null;

      /* Announce the customer's change exactly like the theme does after its own successful write, so every cart
         listener sees it: the drawer repaints from the bundled sections and the Kopstorm buy box removes the
         accessories of a bundle whose suitcase was just taken out. */
      if (applied && applied.cart && applied.cart.sections) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: applied.cart } })
        );
      }

      await settle(await readCart());
      refreshCartUi();
    });
  }

  function onLineChange(event) {
    if (!isCampaignLineElement(lineItemOf(event))) return;

    const cart = event.detail && event.detail.cart;
    const stillInCart = isCart(cart) && analyse(cart).giftLines.length > 0;
    if (Number(event.detail && event.detail.quantity) !== 0 || stillInCart) pendingDecline = false;
    requestReconcile();
  }

  /* Paint the new numbers at once from the event's cart (only a full cart, it must carry the mark), then verify. */
  function onCartChange(event) {
    const cart = event && event.detail && event.detail.cart;
    if (isCart(cart) && cart.attributes && typeof cart.total_price === 'number') publish(plan(analyse(cart)).view);
    requestReconcile();
  }

  function onAnyCartUpdate() {
    requestReconcile();
  }

  /* ------------------------------------------------------------------ start */

  const startAnalysis = analyseSnapshot(config.snapshot || {}, config.currency);
  if (startAnalysis) publish(plan(startAnalysis).view);

  window.etCartGift = Object.freeze({
    enabled: true,
    thresholdCents: function () { return thresholdCents; },
    qualifyingCents: function (cart) { return analyse(cart).qualifying; },
    view: function () { return currentView ? Object.assign({}, currentView) : null; },
    formatMoney: function (cents) { return formatMoney(cents); },
    reconcile: function () { requestReconcile(0); }
  });

  document.addEventListener('cart:change', onCartChange);
  document.addEventListener('cart:refresh', onAnyCartUpdate);
  document.addEventListener('variant:add', onAnyCartUpdate);
  document.addEventListener('shopify:section:load', onAnyCartUpdate);
  document.addEventListener('line-item:will-change', onLineWillChange);
  document.addEventListener('line-item:change', onLineChange);
  document.addEventListener('line-item:error', onLineError);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) requestReconcile(0);
  });

  if (!window.customElements.get('et-cart-gift')) {
    window.customElements.define('et-cart-gift', EtCartGiftElement);
  }

  /* Product page promo pill (snippets/product-gallery.liquid) outside EUR: fill in the converted campaign threshold. */
  function fillPromoBadges() {
    if (thresholdCents === null) return;
    document.querySelectorAll('[data-et-gift-promo]').forEach(function (node) {
      node.textContent = node.dataset.etGiftPromo.split('[amount]').join(formatMoney(thresholdCents));
    });
  }

  fillPromoBadges();
  document.addEventListener('shopify:section:load', fillPromoBadges);

  /* Page load: the gift is right even if the customer never opens the drawer. An empty cart has nothing to do. */
  if (!config.snapshot || Number(config.snapshot.itemCount) > 0) requestReconcile(0);
})();
